import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import SaveIcon from '@mui/icons-material/Save';
import { useDataProvider, useNotify } from 'react-admin';

const SettingsPage = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [coinDollarValue, setCoinDollarValue] = useState<number>(0.01);
  const [profitRatio, setProfitRatio] = useState<number>(1.5);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await dataProvider.getOne('admin/settings', { id: 'global' });
      setCoinDollarValue(response.data.coinDollarValue);
      setProfitRatio(response.data.profitRatio);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setSnackbar({
        open: true,
        message: 'Ayarlar yüklenirken hata oluştu',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await dataProvider.update('admin/settings', {
        id: 'global',
        data: { coinDollarValue, profitRatio },
        previousData: { id: 'global', coinDollarValue, profitRatio },
      });
      setSnackbar({
        open: true,
        message: 'Ayarlar başarıyla kaydedildi',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      setSnackbar({
        open: true,
        message: 'Ayarlar kaydedilirken hata oluştu',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Aynı formül backend ve uygulamada da kullanılıyor:
  // coin = ceil(maliyet$ × KarOranı ÷ CoinDolarDeğeri)
  const coinFor = (costUsd: number): number => {
    if (!coinDollarValue || coinDollarValue <= 0) return 0;
    return Math.ceil((costUsd * profitRatio) / coinDollarValue);
  };

  // Önizleme için örnek numara maliyetleri (ucuzdan pahalıya)
  const sampleCosts = [0.15, 0.5, 1, 3, 10];

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <SettingsIcon sx={{ mr: 1, color: '#7367f0' }} />
        <Typography variant="h5" fontWeight="bold">
          Coin Ayarları
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {/* Sol: ayarlar */}
        <Card sx={{ maxWidth: 520, flex: '1 1 460px' }}>
          <CardContent>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Bu sayfa, kullanıcıya gösterilen <b>coin (token) fiyatını</b> belirler.
              </Typography>
              <Typography variant="body2">
                Her numaranın coin fiyatı, o numaranın <b>gerçek dolar maliyetinden</b>{' '}
                otomatik hesaplanır. Pahalı numara daha çok coin tutar.
                <br />
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    mt: 1,
                    px: 1,
                    py: 0.5,
                    bgcolor: 'rgba(0,0,0,0.06)',
                    borderRadius: 1,
                    fontFamily: 'monospace',
                  }}
                >
                  coin = maliyet$ × Kâr Oranı ÷ Coin Dolar Değeri
                </Box>
              </Typography>
            </Alert>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                label="Kâr Oranı"
                type="number"
                value={profitRatio}
                onChange={(e) => setProfitRatio(parseFloat(e.target.value) || 0)}
                inputProps={{ step: 0.1, min: 1 }}
                fullWidth
                helperText="Maliyetin kaç katına satıyorsun? 1 = kârsız, 2 = maliyetin 2 katı (%100 kâr), 1.5 = %50 kâr."
              />

              <TextField
                label="Coin Dolar Değeri  (1 coin = $ ?)"
                type="number"
                value={coinDollarValue}
                onChange={(e) =>
                  setCoinDollarValue(parseFloat(e.target.value) || 0)
                }
                inputProps={{ step: 0.001, min: 0 }}
                fullWidth
                helperText="1 coin kaç dolar eder (coin'in birimi). Küçük yaparsan numaralar daha çok coin tutar. Örn: 0.10 = 1 coin 10 cent. Uygulamadaki coin paket fiyatlarınla uyumlu tut."
              />

              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={saving}
                startIcon={
                  saving ? <CircularProgress size={20} /> : <SaveIcon />
                }
                sx={{
                  mt: 1,
                  backgroundColor: '#7367f0',
                  '&:hover': { backgroundColor: '#5e50ee' },
                }}
              >
                {saving ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Sağ: canlı önizleme */}
        <Card sx={{ flex: '1 1 320px', maxWidth: 420, alignSelf: 'flex-start' }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold">
              Canlı Önizleme
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Şu anki değerlerle kullanıcı bir numara için kaç coin öder:
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Numara maliyeti</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Kullanıcı öder
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sampleCosts.map((cost) => (
                  <TableRow key={cost}>
                    <TableCell>${cost.toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {coinFor(cost)} coin
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 1.5 }}
            >
              Not: Sonuç yukarı yuvarlanır; bu yüzden çok ucuz numaralar bile en az
              1 coin tutar. Kârın her numarada Kâr Oranı kadardır.
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsPage;
